import { useCallback, useEffect, useState } from 'react';
import { ActivityIndicator, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { router, useLocalSearchParams, usePathname } from 'expo-router';
import { theme } from '@/constants/theme';
import { RuntimeSessionGuard } from '@/features/unified-learning/runtime/screens/RuntimeSessionGuard';
import {
  buildRuntimePracticePath,
  buildRuntimeReverseSessionPath,
  resolveRuntimeBase,
} from '@/features/unified-learning/runtime/runtimeRoutes';
import { loadPracticeCenterData } from '@/features/unified-learning/runtime/practiceCenterService';

const BG = '#0B0B14';
const SURFACE = '#16142A';
const BORDER = '#2D2B50';
const TEXT = '#F0EFF8';
const TEXT_MED = '#9896B4';
const PRIMARY = theme.colors.primary;

export function ReverseLearningEntryScreen() {
  const { slug, uid, token } = useLocalSearchParams<{ slug: string; uid: string; token?: string }>();
  const runtimeBase = resolveRuntimeBase(usePathname());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [weakCount, setWeakCount] = useState(0);
  const [mistakeCount, setMistakeCount] = useState(0);
  const [hasAttemptHistory, setHasAttemptHistory] = useState(false);

  const safeSlug = typeof slug === 'string' ? slug : '';
  const safeUid = typeof uid === 'string' ? uid : '';
  const safeToken = typeof token === 'string' ? token : undefined;
  const runtimeSession = { uid: safeUid, token: safeToken };
  const canStartReverse = hasAttemptHistory && (weakCount > 0 || mistakeCount > 0);

  const load = useCallback(async () => {
    if (!safeSlug || !safeUid) {
      setError('חסרים נתונים כדי לפתוח את מסלול החזרה.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const center = await loadPracticeCenterData(safeSlug, safeUid);
      setWeakCount(center.weakLessonIds.length);
      setMistakeCount(center.recentMistakeScenarioIds.length);
      setHasAttemptHistory(center.hasAttemptHistory);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'לא הצלחנו לטעון את מסלול החזרה.');
    } finally {
      setLoading(false);
    }
  }, [safeSlug, safeUid]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!safeSlug || !safeUid) {
    return <RuntimeSessionGuard runtimeBase={runtimeBase} slug={safeSlug} />;
  }

  if (loading) {
    return (
      <View style={s.center}>
        <ActivityIndicator size="large" color={PRIMARY} />
      </View>
    );
  }

  if (error) {
    return (
      <View style={s.center}>
        <View style={s.errorCard}>
          <Text style={s.errorTitle}>לא הצלחנו לטעון את מסלול החזרה</Text>
          <Text style={s.error}>{error}</Text>
          <View style={s.actionGroup}>
            <Pressable style={s.primaryBtn} onPress={() => void load()}>
              <Text style={s.primaryBtnText}>נסה שוב</Text>
            </Pressable>
            <Pressable
              style={s.secondaryBtn}
              onPress={() => router.push(buildRuntimePracticePath(runtimeBase, safeSlug, runtimeSession) as any)}
            >
              <Text style={s.secondaryBtnText}>חזרה למרכז התרגול</Text>
            </Pressable>
          </View>
        </View>
      </View>
    );
  }

  return (
    <ScrollView style={s.root} contentContainerStyle={s.content}>
      <Text style={s.eyebrow}>KURSI LEARN · מסלול חזרה</Text>
      <Text style={s.title}>חזרה ממוקדת</Text>
      <Text style={s.subtitle}>מתחילים מהטעויות האחרונות, סוגרים פערים קריטיים, ואז בודקים אם אפשר להתקדם בביטחון.</Text>

      <View style={s.card}>
        <Text style={s.cardTitle}>מה נתרגל עכשיו</Text>
        <Text style={s.row}>שיעורים חלשים שנכנסים לסשן: {weakCount}</Text>
        <Text style={s.row}>ניסיונות חלשים/מסוכנים אחרונים: {mistakeCount}</Text>
        <Text style={s.row}>פורמט הסשן: 8 אינטראקציות לסגירת פערים</Text>
        {!hasAttemptHistory && (
          <Text style={s.warning}>אין עדיין מספיק נתוני תרגול אישיים. נתחיל מתרגול קצר כדי לבנות פרופיל חולשות אמיתי.</Text>
        )}
      </View>

      {canStartReverse ? (
        <Pressable
          style={s.primaryBtn}
          onPress={() => router.push(buildRuntimeReverseSessionPath(runtimeBase, safeSlug, runtimeSession, { target: '8' }) as any)}
        >
          <Text style={s.primaryBtnText}>התחל סשן סגירת פערים</Text>
        </Pressable>
      ) : (
        <Pressable
          style={s.primaryBtn}
          onPress={() => router.push(buildRuntimePracticePath(runtimeBase, safeSlug, runtimeSession) as any)}
        >
          <Text style={s.primaryBtnText}>בנה קודם היסטוריית תרגול</Text>
        </Pressable>
      )}
    </ScrollView>
  );
}

const s = StyleSheet.create({
  root: { flex: 1, backgroundColor: BG },
  content: { padding: 20, gap: 14, maxWidth: 760, width: '100%', alignSelf: 'center' as any, paddingTop: 42, paddingBottom: 40 },
  center: { flex: 1, backgroundColor: BG, alignItems: 'center', justifyContent: 'center' },
  eyebrow: { color: PRIMARY, fontFamily: theme.fonts.bold, letterSpacing: 1, fontSize: 12 },
  title: { color: TEXT, fontFamily: theme.fonts.bold, fontSize: 30, textAlign: 'right' },
  subtitle: { color: TEXT_MED, fontFamily: theme.fonts.regular, textAlign: 'right', lineHeight: 22 },
  card: { backgroundColor: SURFACE, borderWidth: 1, borderColor: BORDER, borderRadius: 14, padding: 14, gap: 8 },
  cardTitle: { color: TEXT, fontFamily: theme.fonts.bold, fontSize: 18, textAlign: 'right' },
  row: { color: TEXT_MED, fontFamily: theme.fonts.regular, textAlign: 'right' },
  primaryBtn: { backgroundColor: PRIMARY, borderRadius: 12, alignItems: 'center', paddingVertical: 12 },
  primaryBtnText: { color: '#fff', fontFamily: theme.fonts.bold, fontSize: 16 },
  warning: { color: '#FBBF24', fontFamily: theme.fonts.regular, textAlign: 'right', lineHeight: 21 },
  errorCard: { width: '100%', maxWidth: 520, backgroundColor: SURFACE, borderRadius: 14, borderWidth: 1, borderColor: BORDER, padding: 14, gap: 10 },
  errorTitle: { color: TEXT, fontFamily: theme.fonts.bold, textAlign: 'right', fontSize: 19 },
  actionGroup: { gap: 8 },
  secondaryBtn: { borderWidth: 1, borderColor: BORDER, borderRadius: 12, alignItems: 'center', paddingVertical: 11 },
  secondaryBtnText: { color: TEXT, fontFamily: theme.fonts.bold },
  error: { color: '#FCA5A5', fontFamily: theme.fonts.bold, textAlign: 'center' },
});
